import { create } from 'zustand'
import type { SubjectCode } from '../types'

interface SelectedTopicInfo {
  topicCode: string
  topicName: string
  subjectCode?: string
  subjectName?: string
}

interface PYQAnalyticsState {
  // Year range filter
  yearRangeStart: number
  yearRangeEnd: number

  // Subject selection
  selectedSubject: SubjectCode

  // Modal states
  selectedUnitCode: string | null
  selectedTopicCode: string | null
  selectedTopicInfo: SelectedTopicInfo | null

  // Comparison (max 10 topics)
  comparisonTopicCodes: string[]

  // Actions
  setYearRange: (start: number, end: number) => void
  setSelectedSubject: (subject: SubjectCode) => void
  setSelectedUnitCode: (unitCode: string | null) => void
  setSelectedTopicCode: (topicCode: string | null) => void
  setSelectedTopic: (topicInfo: SelectedTopicInfo | null) => void
  addComparisonTopic: (topicCode: string) => void
  removeComparisonTopic: (topicCode: string) => void
  clearComparisonTopics: () => void
  resetFilters: () => void
}

const currentYear = new Date().getFullYear()
const defaultYearStart = currentYear - 10

export const usePYQAnalyticsStore = create<PYQAnalyticsState>((set, get) => ({
  // Initial state
  yearRangeStart: defaultYearStart,
  yearRangeEnd: currentYear,
  selectedSubject: 'NEET_UG_PHY',
  selectedUnitCode: null,
  selectedTopicCode: null,
  selectedTopicInfo: null,
  comparisonTopicCodes: [],

  // Actions
  setYearRange: (start, end) => {
    if (start <= end && start >= 1990 && end <= currentYear) {
      set({ yearRangeStart: start, yearRangeEnd: end })
    }
  },

  setSelectedSubject: (subject) => {
    set({ selectedSubject: subject })
  },

  setSelectedUnitCode: (unitCode) => {
    set({ selectedUnitCode: unitCode })
  },

  setSelectedTopicCode: (topicCode) => {
    set({ selectedTopicCode: topicCode })
  },

  setSelectedTopic: (topicInfo) => {
    set({
      selectedTopicCode: topicInfo?.topicCode || null,
      selectedTopicInfo: topicInfo,
    })
  },

  addComparisonTopic: (topicCode) => {
    const current = get().comparisonTopicCodes
    if (current.length < 10 && !current.includes(topicCode)) {
      set({ comparisonTopicCodes: [...current, topicCode] })
    }
  },

  removeComparisonTopic: (topicCode) => {
    set({
      comparisonTopicCodes: get().comparisonTopicCodes.filter((code) => code !== topicCode),
    })
  },

  clearComparisonTopics: () => {
    set({ comparisonTopicCodes: [] })
  },

  resetFilters: () => {
    set({
      yearRangeStart: defaultYearStart,
      yearRangeEnd: currentYear,
      selectedSubject: 'NEET_UG_PHY',
      selectedUnitCode: null,
      selectedTopicCode: null,
      selectedTopicInfo: null,
      comparisonTopicCodes: [],
    })
  },
}))
